/**
 * Utility functions for formatting data in the admin UI
 */

/**
 * Format time with timezone information
 * @param hour - Hour in 24-hour format
 * @param minute - Minute
 * @returns Formatted time string with UTC and local timezone
 */
export function formatTimeWithTimezone(hour: string, minute: string): string {
  try {
    const utcHour = parseInt(hour);
    const utcMinute = parseInt(minute);
    if (isNaN(utcHour) || isNaN(utcMinute) || utcHour < 0 || utcHour > 23 || utcMinute < 0 || utcMinute > 59) {
      return `${hour}:${minute.padStart(2, '0')} UTC`;
    }
    const utcDate = new Date();
    utcDate.setUTCHours(utcHour, utcMinute, 0, 0);
    const utcTime = utcDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC', hour12: true });
    let localTime = '';
    let timezoneAbbr = '';
    try {
      localTime = utcDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const timezoneParts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(utcDate);
      timezoneAbbr = timezoneParts.find(part => part.type === 'timeZoneName')?.value || timezone;
      return `${utcTime} UTC (${localTime} ${timezoneAbbr})`;
    } catch (localError) {
      return `${utcTime} UTC`;
    }
  } catch (error) {
    return `${hour}:${minute.padStart(2, '0')} UTC`;
  }
}

/**
 * Format a cron schedule expression into a human-readable string
 * @param value - Cron expression
 * @returns Human-readable schedule description
 */
export function formatCronSchedule(value: string): string {
  try {
    const parts = value.split(' ');
    if (parts.length < 6) return 'Invalid schedule format';
    const [minute, hour, dayOfMonth, , dayOfWeek] = parts;
    
    if (minute.startsWith('0/') && hour === '*') {
      const interval = minute.split('/')[1];
      return `Every ${interval} minutes`;
    }
    if (hour === '*' && minute === '0') return 'Every hour';
    if (hour === '*/6' && minute === '0') return 'Every 6 hours';
    if (dayOfWeek === '?' && dayOfMonth === '*') {
      if (hour === '0' && minute === '0') return `Daily at midnight UTC (${formatTimeWithTimezone('0', '0')})`;
      return `Daily at ${formatTimeWithTimezone(hour, minute)}`;
    }
    if (dayOfWeek.includes('-') && dayOfWeek.includes('MON-FRI')) {
      return `Weekdays at ${formatTimeWithTimezone(hour, minute)}`;
    }
    if (dayOfWeek.includes('SAT,SUN')) {
      return `Weekends at ${formatTimeWithTimezone(hour, minute)}`;
    }
    if (dayOfWeek === 'MON' && dayOfMonth === '?') {
      return `Weekly on Monday at ${formatTimeWithTimezone(hour, minute)}`;
    }
    if (dayOfMonth === '1' && dayOfWeek === '?') {
      return `Monthly on the 1st at ${formatTimeWithTimezone(hour, minute)}`;
    }
    return `Custom schedule: ${value}`;
  } catch (error) {
    return `Custom schedule: ${value}`;
  }
} 